import { datadogRum } from "@datadog/browser-rum"
import { reactPlugin } from "@datadog/browser-rum-react"
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { supportedLanguages } = require("../util/language")

const LANGUAGES: Set<string> = new Set(supportedLanguages)

// Top-level page segments from src/pages; anything before these in a URL is a
// language and/or country prefix added by gatsby-plugin-react-i18next
const PAGE_ROOTS = new Set([
  "restaurants",
  "profile",
  "legal",
  "download-app",
  "order-status",
  "card-form",
  "nav",
  "t",
  "password-recovery",
  "404",
])

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Collapses concrete URLs into route patterns so Datadog groups views per
// route instead of creating a new view name for every order and restaurant,
// e.g. /dnk/restaurants/bones-taastrup/checkout/<uuid>/payment
//   -> /:countryCode/restaurants/:slug/checkout/:orderId/payment
//
// The language prefix is dropped rather than named: i18next redirects
// /dnk/... to /en/dnk/..., which would otherwise produce two view names for
// one page visit and split its metrics in half. Language is tracked as a
// global context property instead.
export const toDatadogViewName = (pathname: string): string => {
  const allSegments = pathname.split("/").filter(Boolean)
  const segments =
    allSegments.length > 0 && LANGUAGES.has(allSegments[0])
      ? allSegments.slice(1)
      : allSegments
  if (segments.length === 0) {
    return "/"
  }

  const rootIndex = segments.findIndex((segment) => PAGE_ROOTS.has(segment))
  const root = rootIndex === -1 ? undefined : segments[rootIndex]

  const named = segments.map((segment, index) => {
    if (rootIndex > 0 && index < rootIndex) {
      const isCountryCode =
        index === rootIndex - 1 &&
        (root === "restaurants" || root === "profile")
      return isCountryCode ? ":countryCode" : ":lang"
    }
    if (UUID_PATTERN.test(segment)) {
      return ":orderId"
    }
    const previous = segments[index - 1]
    if (previous === "restaurants") {
      return ":slug"
    }
    if (previous === "t") {
      return ":displayId"
    }
    if (previous === "password-recovery") {
      return ":organizationKeyName"
    }
    return segment
  })

  return `/${named.join("/")}`
}

export const initDatadogRum = () => {
  datadogRum.init({
    applicationId: "19e89cc2-8b97-41bf-83a8-c0f807a8b473",
    // Public write-only ingestion token, safe to ship in the bundle
    clientToken: "pub36369da5ce1ad947c3ce1ccae23e98b3",
    site: "datadoghq.eu",
    service: "heapsgo-web",
    env: process.env.GATSBY_NODE_ENV,
    version: process.env.GATSBY_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    // Views are started from onRouteUpdate in gatsby-browser.js with
    // route-pattern names from toDatadogViewName
    trackViewsManually: true,
    // We render customer and payment data; never record user input in replays
    defaultPrivacyLevel: "mask-user-input",
    // TODO(HEA-3229): enable distributed tracing to drinks-backend once the
    // API allows the x-datadog-* / traceparent headers in CORS:
    // allowedTracingUrls: [process.env.GATSBY_HEAPS_BACKEND_URL ?? ""],
    plugins: [reactPlugin({ router: false })],
  })

  // Global context lands on every RUM event - errors, actions, views,
  // resources - so all of them are filterable per white-label site
  const customer = toCustomer(window.location.hostname)
  if (customer) {
    datadogRum.setGlobalContextProperty("customer", customer)
  }

  if (process.env.GATSBY_PARTNER_TITLE) {
    datadogRum.setGlobalContextProperty(
      "partner",
      process.env.GATSBY_PARTNER_TITLE
    )
  }

  // Set GATSBY_DATADOG_TEST_SESSION=true when deliberately generating events
  // to verify the pipeline. Dashboards and monitors must exclude
  // @context.isTestSession:true so manual verification never triggers alerts
  // or skews error rates.
  if (process.env.GATSBY_DATADOG_TEST_SESSION === "true") {
    datadogRum.setGlobalContextProperty("isTestSession", true)
  }
}

const HEAPS_APEX_DOMAINS = [".heapsgo.com", ".heapsapp.com"]

/**
 * Which white-label site this is, taken from the hostname so it is always
 * present rather than depending on build configuration:
 *   bones.heapsgo.com        -> "bones"
 *   vendor1-staging.heapsgo.com -> "vendor1"  (env already says staging)
 *   taw.bones.dk             -> "bones"       (partner's own domain)
 * Returns undefined on localhost, where there is no customer.
 */
export const toCustomer = (
  hostname: string | undefined
): string | undefined => {
  // This runs inside error reporting, so it must never be the thing that throws
  if (!hostname) {
    return undefined
  }
  const labels = hostname.split(".")
  if (labels.length < 3) {
    return undefined
  }
  if (HEAPS_APEX_DOMAINS.some((apex) => hostname.endsWith(apex))) {
    return labels[0].replace(/-staging$/, "")
  }
  // On a partner's own domain the registrable name identifies the customer
  return labels[labels.length - 2]
}

export const toLanguage = (pathname: string): string | undefined => {
  const first = pathname.split("/").filter(Boolean)[0]
  return first && LANGUAGES.has(first) ? first : undefined
}

export const trackDatadogView = (pathname: string) => {
  // Kept out of the view name (see toDatadogViewName) but still filterable
  const language = toLanguage(pathname)
  if (language) {
    datadogRum.setGlobalContextProperty("language", language)
  }
  datadogRum.startView({ name: toDatadogViewName(pathname) })
}

/**
 * Funnel signals (e.g. "payment failed screen shown") — RUM custom actions,
 * usable for failure-rate monitors independently of whether an exception
 * was thrown.
 */
export const trackDatadogAction = (
  name: string,
  context?: Record<string, unknown>
) => {
  datadogRum.addAction(name, context)
}
